import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { GridViewModel } from '@models/module/grid';
import { HttpService } from '@shared/services';

import {
  EmailLinkModel,
  ModuleSignature,
  ModuleWorkflowsResponse,
  TransitionGroup,
  TransitionGroupOrderPayload,
} from './module-config.model';

@Injectable({ providedIn: 'root' })
export class ModuleConfigService {
  private readonly http = inject(HttpService);

  private moduleHeaders(moduleId: number) {
    return { moduleId: moduleId.toString() };
  }

  getModuleWorkflows(moduleId: number): Observable<ModuleWorkflowsResponse> {
    return this.http.get<ModuleWorkflowsResponse>('_api/WorkflowDefinition/ModuleWorkflows', {
      useLoader: false,
      httpOptions: { headers: this.moduleHeaders(moduleId) },
    });
  }

  searchSignatures(moduleId: number, workflowId: number): Observable<ModuleSignature[]> {
    const params = this.http.buildHttpParams({ workflowId });
    return this.http
      .post<
        GridViewModel<ModuleSignature[]>
      >('_api/ModuleSignature/SearchUserRole', {}, { useLoader: false, httpOptions: { params, headers: this.moduleHeaders(moduleId) } })
      .pipe(map(result => result?.data ?? []));
  }

  createSignature(body: ModuleSignature) {
    return this.http.post('_api/ModuleSignature/CreateUserRole', body, { useLoader: false });
  }

  updateSignature(body: ModuleSignature) {
    return this.http.put('_api/ModuleSignature/UpdateUserRole', body, { useLoader: false });
  }

  /** The endpoint takes a list of ids, even when deleting one row. */
  deleteSignature(id: number) {
    return this.http.put('_api/ModuleSignature/DeleteUserRoles', [id], { useLoader: false });
  }

  searchTransitionGroups(moduleId: number, workflowId: number): Observable<TransitionGroup[]> {
    const params = this.http.buildHttpParams({ workflowId });
    return this.http
      .post<
        GridViewModel<TransitionGroup[]>
      >('_api/ModuleGroupTransitionSign/Search', {}, { useLoader: false, httpOptions: { params, headers: this.moduleHeaders(moduleId) } })
      .pipe(map(result => result?.data ?? []));
  }

  addTransitionGroup(moduleId: number, workflowId: number, body: TransitionGroup) {
    const params = this.http.buildHttpParams({ workflowId });
    return this.http.post('_api/ModuleGroupTransitionSign/Add', body, {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  editTransitionGroup(moduleId: number, workflowId: number, body: TransitionGroup) {
    const params = this.http.buildHttpParams({ workflowId });
    return this.http.put('_api/ModuleGroupTransitionSign/Edit', body, {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  deleteTransitionGroup(moduleId: number, id: number) {
    const params = this.http.buildHttpParams({ id });
    return this.http.put(
      '_api/ModuleGroupTransitionSign/Delete',
      {},
      {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      },
    );
  }

  syncTransitionGroupOrder(moduleId: number, payload: TransitionGroupOrderPayload) {
    return this.http.put('_api/ModuleGroupTransitionSign/SyncOrder', payload, {
      useLoader: false,
      httpOptions: { headers: this.moduleHeaders(moduleId) },
    });
  }

  getEmailLink(moduleId: number): Observable<EmailLinkModel> {
    return this.http.get<EmailLinkModel>('_api/LinkCorrespondenceToModule/Get', {
      useLoader: false,
      httpOptions: { headers: this.moduleHeaders(moduleId) },
    });
  }

  createEmailLink(moduleId: number, body: EmailLinkModel): Observable<number> {
    return this.http.post<number>('_api/LinkCorrespondenceToModule/Create', body, {
      useLoader: false,
      httpOptions: { headers: this.moduleHeaders(moduleId) },
    });
  }

  updateEmailLink(moduleId: number, body: EmailLinkModel) {
    return this.http.put('_api/LinkCorrespondenceToModule/Update', body, {
      useLoader: false,
      httpOptions: { headers: this.moduleHeaders(moduleId) },
    });
  }

  deleteEmailLink(moduleId: number, id: number) {
    const params = this.http.buildHttpParams({ id });
    return this.http.put(
      '_api/LinkCorrespondenceToModule/Delete',
      {},
      {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      },
    );
  }
}
